// VexFlow rendering utilities

import { Renderer, Stave, StaveNote, Voice, Formatter, Accidental } from "vexflow";
import { Midi, Note } from "tonal";
import getOptimalEnharmonics from "./getEnharmonics";
import { getConfigValue } from "./config";

// Use config values for all layout constants
const STARTING_PITCH = getConfigValue('audio.pitch.startingPitch', 60);

/**
 * Creates VexFlow notes from a scale array
 * @param {Array} scale - Array of semitone intervals
 * @returns {Object} { midiNotes, optimizedNoteNames, staveNotes }
 */
export const createNotesFromScale = (scale) => {
  // Convert scale to MIDI notes
  const midiNotes = scale.reduce((acc, step) => {
    acc.push(acc[acc.length - 1] + step);
    return acc;
  }, [STARTING_PITCH]);

  // Get note names and optimize enharmonics
  const rawNoteNames = midiNotes.map(midiNote => Midi.midiToNoteName(midiNote));
  const optimizedNoteNames = getOptimalEnharmonics(rawNoteNames);

  // Create VexFlow StaveNote objects
  const staveNotes = optimizedNoteNames.map((noteName, index) => {
    const note = Note.get(noteName);
    const key = `${note.pc.toLowerCase()}/${note.oct}`;
    
    const staveNote = new StaveNote({
      clef: "treble",
      keys: [key],
      duration: "q"
    });
    
    // Add accidentals with enhanced logic
    if (note.acc) {
      staveNote.addModifier(new Accidental(note.acc));
    } else if (index > 0) {
      const prevNote = Note.get(optimizedNoteNames[index - 1]);
      if (prevNote.letter === note.letter && prevNote.acc) {
        staveNote.addModifier(new Accidental('n'));
      } else if (prevNote.acc && Math.abs(prevNote.chroma - note.chroma) === 1) {
        const prevLetter = prevNote.letter;
        const currentLetter = note.letter;
        
        if ((prevLetter === 'D' && currentLetter === 'E' && prevNote.acc === '#') ||
            (prevLetter === 'E' && currentLetter === 'F' && prevNote.acc === '#') ||
            (prevLetter === 'A' && currentLetter === 'B' && prevNote.acc === '#') ||
            (prevLetter === 'B' && currentLetter === 'C' && prevNote.acc === '#')) {
          staveNote.addModifier(new Accidental('n'));
        }
      }
    }
    
    return staveNote;
  });

  return { midiNotes, optimizedNoteNames, staveNotes };
};

/**
 * Sets up VexFlow renderer and context
 * @param {HTMLElement} container - DOM container for the SVG
 * @returns {Object} { renderer, context, stave }
 */
export const setupVexFlowRenderer = (container) => {
  // Get container width and make responsive using config values
  const containerWidth = container.offsetWidth || getConfigValue('visual.notation.maxWidth', 460);
  const svgWidth = Math.min(
    getConfigValue('visual.notation.maxWidth', 460), 
    containerWidth - getConfigValue('visual.notation.containerMargin', 20)
  );
  const svgHeight = getConfigValue('visual.notation.svgHeight', 120);
  
  const renderer = new Renderer(container, Renderer.Backends.SVG);
  renderer.resize(svgWidth, svgHeight);
  const context = renderer.getContext();
  
  // Style the SVG for responsiveness
  const svg = container.querySelector('svg');
  if (svg) {
    svg.style.background = 'transparent';
    svg.style.maxWidth = '100%';
    svg.style.height = 'auto';
  }
  
  // Create and draw stave with responsive width using config values
  const staveWidth = Math.min(
    getConfigValue('visual.notation.maxWidth', 460) - 40, 
    svgWidth - getConfigValue('visual.notation.staveMargin', 20)
  );
  const stave = new Stave(
    getConfigValue('visual.notation.staveLeftOffset', 0), 
    getConfigValue('visual.notation.staveTopOffset', 20), 
    staveWidth
  );
  stave.addClef("treble").setContext(context).draw();
  
  return { renderer, context, stave, svgWidth };
};

/**
 * Renders notes using VexFlow Voice and Formatter
 * @param {Array} notes - Array of StaveNote objects
 * @param {Object} context - VexFlow rendering context
 * @param {Object} stave - VexFlow stave object
 * @param {number} formatWidth - Width for formatting (optional)
 */
export const renderNotesWithVexFlow = (notes, context, stave, formatWidth = getConfigValue('visual.notation.formatWidth', 360)) => {
  const voice = new Voice({ num_beats: notes.length, beat_value: 4 });
  voice.addTickables(notes);
  
  new Formatter().joinVoices([voice]).format([voice], formatWidth);
  voice.draw(context, stave);
};